import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { showErrorToast } from '../../../../utils/notificationUtils';
import { useTranslation } from 'react-i18next';

const ChiefComplaintTab = forwardRef(({ inpatientId, readOnly = false }, ref) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [complaint, setComplaint] = useState('');

    // Store latest values in refs for cleanup/unmount access
    const complaintRef = useRef('');
    const soapDataRef = useRef(null);
    const initialComplaintRef = useRef('');
    const hasUnsavedChanges = useRef(false);

    const saveData = useCallback(async (currentComplaint, baseData) => {
        if (readOnly) return;
        try {
            const token = localStorage.getItem('access_token');

            // Always fetch the latest SOAP data to ensure we have the most up-to-date information
            // This prevents overwriting data that was saved by other tabs
            let latestSoapData = baseData;
            try {
                const fetchResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/soap-notes/inpatient-visit/${inpatientId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json'
                    }
                });
                if (fetchResponse.ok) {
                    const fetchJson = await fetchResponse.json();
                    if (fetchJson.data && fetchJson.data.length > 0) {
                        latestSoapData = fetchJson.data[0];
                    }
                }
            } catch (fetchErr) {
                console.error("Failed to fetch latest SOAP data before save:", fetchErr);
                // Fall back to baseData if fetch fails
                if (!latestSoapData) {
                    latestSoapData = baseData || {};
                }
            }

            // Construct payload preserving existing data - only include fields that exist or use defaults
            const payload = {
                inpatient_visit_id: parseInt(inpatientId),
                serial: latestSoapData?.serial || null,
                date_time: latestSoapData?.date_time || new Date().toISOString().slice(0, 16),
                owner_complaint: currentComplaint || '', // Always use current complaint value
                objective: latestSoapData?.objective || '',
                severity: latestSoapData?.severity || '',
                initial_diagnosis: latestSoapData?.initial_diagnosis || '',
                assessment: latestSoapData?.assessment || '',
                plan: latestSoapData?.plan || '',
                diagnosis_history: latestSoapData?.diagnosis_history || []
            };

            const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/soap-notes`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload),
                keepalive: true // Important for fetch on unmount
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error("Save failed:", errorData);
                throw new Error('Failed to save');
            }

            // Update the ref with the saved data to keep it in sync
            soapDataRef.current = { ...latestSoapData, owner_complaint: currentComplaint };
        } catch (err) {
            console.error("Auto-save failed:", err);
        }
    }, [inpatientId]);

    const fetchSoapData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/soap-notes/inpatient-visit/${inpatientId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                }
            });

            if (response.ok) {
                const json = await response.json();
                if (json.data && json.data.length > 0) {
                    const latest = json.data[0];
                    soapDataRef.current = latest;

                    const existingComplaint = latest.owner_complaint || '';
                    setComplaint(existingComplaint);
                    complaintRef.current = existingComplaint;
                    initialComplaintRef.current = existingComplaint;
                } else {
                    // Initialize with minimal structure to ensure saves work
                    const emptyData = {
                        serial: null,
                        date_time: new Date().toISOString().slice(0, 16),
                        objective: '',
                        severity: '',
                        initial_diagnosis: '',
                        assessment: '',
                        plan: '',
                        diagnosis_history: []
                    };
                    soapDataRef.current = emptyData;
                    setComplaint('');
                    complaintRef.current = '';
                    initialComplaintRef.current = '';
                }
            }
        } catch (err) {
            console.error(err);
            showErrorToast(t('receptionInpatients.details.chiefComplaint.toasts.loadSoapFailed', { defaultValue: 'Failed to load existing SOAP data' }));
        } finally {
            setLoading(false);
        }
    }, [inpatientId]);

    useImperativeHandle(ref, () => ({
        save: async () => {
            if (readOnly) return;
            if (soapDataRef.current) {
                await saveData(complaintRef.current, soapDataRef.current);
            }
        }
    }));

    useEffect(() => {
        if (inpatientId) {
            fetchSoapData();
        }
    }, [inpatientId, fetchSoapData]);

    // Keep refs updated
    useEffect(() => {
        complaintRef.current = complaint;
        if (complaint !== initialComplaintRef.current) {
            hasUnsavedChanges.current = true;
        } else {
            hasUnsavedChanges.current = false;
        }
    }, [complaint]);

    // Save on unmount (skip when view-only)
    useEffect(() => {
        return () => {
            if (readOnly) return;
            if (hasUnsavedChanges.current && soapDataRef.current) {
                saveData(complaintRef.current, soapDataRef.current);
            }
        };
    }, [saveData, readOnly]);

    if (loading) {
        return (
            <div className="tab-loading">
                <div className="spinner"></div>
                <p>{t('receptionInpatients.details.loading', { defaultValue: 'Loading inpatient details...' })}</p>
            </div>
        );
    }

    return (
        <div className="chief-complaint-tab tab-content-inner" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="tab-header" style={{ marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>
                    {t('receptionInpatients.details.chiefComplaint.title', { defaultValue: 'Pet Guardian Complaint & Pet History' })}
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.9rem', color: '#64748b' }}>
                    {t('receptionInpatients.details.chiefComplaint.description', { defaultValue: 'Enter the details below. This will be automatically reflected in the SOAP notes.' })}
                </p>
            </div>

            <div className="editor-container" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <textarea
                    value={complaint}
                    onChange={(e) => !readOnly && setComplaint(e.target.value)}
                    disabled={readOnly}
                    placeholder={t('receptionInpatients.details.chiefComplaint.placeholder', { defaultValue: 'Enter complaint & history...' })}
                    style={{
                        flex: 1,
                        width: '100%',
                        resize: 'none',
                        padding: '16px',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        fontSize: '1rem',
                        lineHeight: '1.6',
                        fontFamily: 'inherit',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        backgroundColor: readOnly ? '#F9FAFB' : '#FFFFFF',
                        cursor: readOnly ? 'not-allowed' : 'text'
                    }}
                    onFocus={(e) => !readOnly && (e.target.style.borderColor = '#00D4B3')}
                    onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        if (readOnly) return;
                        // Optional: Trigger save on blur as well
                        if (hasUnsavedChanges.current && soapDataRef.current) {
                            saveData(complaint, soapDataRef.current);
                            hasUnsavedChanges.current = false;
                            initialComplaintRef.current = complaint;
                        }
                    }}
                />
            </div>
        </div>
    );
});

export default ChiefComplaintTab;
